// canvasSlice — Redux slice for cross-pillar canvas interactions.
// Manages CanvasSession, zoom levels, overlay toggling, and interaction event logging.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CanvasSession,
  ZoomLevel,
  OverlayLayer,
  CanvasInteractionType,
} from '../../types/index.ts';
import type { RootState } from '../index.ts';

// ---------------------------------------------------------------------------
// Interaction event record
// ---------------------------------------------------------------------------

export interface CanvasInteractionRecord {
  id: string;
  type: CanvasInteractionType;
  timestamp: string;
  detail: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MIN_SCALE = 0.15;
export const MAX_SCALE = 3.0;

/** Maximum retained interaction records — older entries are dropped (unbounded-growth guard). */
export const MAX_INTERACTION_LOG = 500;

function scaleToZoomLevel(scale: number): ZoomLevel {
  if (scale < 0.5) return 'ecosystem';
  if (scale <= 1.2) return 'context';
  return 'element';
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface CanvasState {
  session: CanvasSession | null;
  zoomLevel: ZoomLevel;
  scale: number;
  panX: number;
  panY: number;
  activeOverlays: OverlayLayer[];
  interactionLog: CanvasInteractionRecord[];
  /** Monotonic id counter — survives log trimming so ids never collide. */
  interactionSeq: number;
  focusedContextId: string | null;
  focusedElementId: string | null;
}

const initialState: CanvasState = {
  session: null,
  zoomLevel: 'context',
  scale: 1,
  panX: 0,
  panY: 0,
  activeOverlays: ['simulation'],
  interactionLog: [],
  interactionSeq: 0,
  focusedContextId: null,
  focusedElementId: null,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const canvasSlice = createSlice({
  name: 'canvas',
  initialState,
  reducers: {
    startCanvasSession(state, action: PayloadAction<{ sessionId: string; tenantId: string }>) {
      const now = new Date().toISOString();
      state.session = {
        sessionId: action.payload.sessionId,
        tenantId: action.payload.tenantId,
        startedAt: now,
        zoomLevel: state.zoomLevel,
        activeOverlays: [...state.activeOverlays],
        interactionCount: 0,
        lastInteractionAt: now,
      };
    },

    setZoomLevel(state, action: PayloadAction<ZoomLevel>) {
      state.zoomLevel = action.payload;
      if (state.session) {
        state.session.zoomLevel = action.payload;
      }
      // Reset focus when zooming out
      if (action.payload === 'ecosystem') {
        state.focusedContextId = null;
        state.focusedElementId = null;
      } else if (action.payload === 'context') {
        state.focusedElementId = null;
      }
    },

    toggleOverlay(state, action: PayloadAction<OverlayLayer>) {
      const layer = action.payload;
      const idx = state.activeOverlays.indexOf(layer);
      if (idx >= 0) {
        state.activeOverlays.splice(idx, 1);
      } else {
        state.activeOverlays.push(layer);
      }
      if (state.session) {
        state.session.activeOverlays = [...state.activeOverlays];
      }
    },

    setActiveOverlays(state, action: PayloadAction<OverlayLayer[]>) {
      state.activeOverlays = action.payload;
      if (state.session) {
        state.session.activeOverlays = [...action.payload];
      }
    },

    focusContext(state, action: PayloadAction<string | null>) {
      state.focusedContextId = action.payload;
      state.focusedElementId = null;
      if (action.payload) {
        state.zoomLevel = 'context';
      }
    },

    focusElement(state, action: PayloadAction<string | null>) {
      state.focusedElementId = action.payload;
      if (action.payload) {
        state.zoomLevel = 'element';
      }
    },

    recordInteraction(
      state,
      action: PayloadAction<{
        type: CanvasInteractionType;
        detail: Record<string, unknown>;
      }>,
    ) {
      const now = new Date().toISOString();
      state.interactionSeq += 1;
      const record: CanvasInteractionRecord = {
        id: `interaction-${state.interactionSeq}`,
        type: action.payload.type,
        timestamp: now,
        detail: action.payload.detail,
      };
      state.interactionLog.push(record);
      // Cap the log — keep only the most recent MAX_INTERACTION_LOG records.
      if (state.interactionLog.length > MAX_INTERACTION_LOG) {
        state.interactionLog.splice(0, state.interactionLog.length - MAX_INTERACTION_LOG);
      }

      if (state.session) {
        state.session.interactionCount++;
        state.session.lastInteractionAt = now;
      }
    },

    clearInteractionLog(state) {
      state.interactionLog = [];
    },

    setScale(state, action: PayloadAction<number>) {
      state.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, action.payload));
      state.zoomLevel = scaleToZoomLevel(state.scale);
      if (state.session) {
        state.session.zoomLevel = state.zoomLevel;
      }
    },

    setPan(state, action: PayloadAction<{ x: number; y: number }>) {
      state.panX = action.payload.x;
      state.panY = action.payload.y;
    },

    zoomToPoint(
      state,
      action: PayloadAction<{
        newScale: number;
        pointX: number;
        pointY: number;
      }>,
    ) {
      const { pointX, pointY, newScale: rawScale } = action.payload;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, rawScale));
      const oldScale = state.scale;

      // Adjust pan so the point under the cursor stays fixed
      state.panX = pointX - (pointX - state.panX) * (newScale / oldScale);
      state.panY = pointY - (pointY - state.panY) * (newScale / oldScale);
      state.scale = newScale;
      state.zoomLevel = scaleToZoomLevel(newScale);
      if (state.session) {
        state.session.zoomLevel = state.zoomLevel;
      }
    },

    resetView(state) {
      state.scale = 1;
      state.panX = 0;
      state.panY = 0;
      state.zoomLevel = scaleToZoomLevel(1);
    },

    fitToView(
      state,
      action: PayloadAction<{
        contentW: number;
        contentH: number;
        containerW: number;
        containerH: number;
      }>,
    ) {
      const { contentW, contentH, containerW, containerH } = action.payload;
      if (contentW <= 0 || contentH <= 0) return;
      const scaleX = containerW / contentW;
      const scaleY = containerH / contentH;
      const fitScale = Math.min(scaleX, scaleY, MAX_SCALE);
      state.scale = Math.max(MIN_SCALE, fitScale * 0.95); // 5% padding
      state.panX = (containerW - contentW * state.scale) / 2;
      state.panY = (containerH - contentH * state.scale) / 2;
      state.zoomLevel = scaleToZoomLevel(state.scale);
    },
  },
});

export const {
  startCanvasSession,
  setZoomLevel,
  setScale,
  setPan,
  zoomToPoint,
  resetView,
  fitToView,
  toggleOverlay,
  setActiveOverlays,
  focusContext,
  focusElement,
  recordInteraction,
  clearInteractionLog,
} = canvasSlice.actions;

export default canvasSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectCanvasSession = (state: RootState) => state.canvas.session;
export const selectZoomLevel = (state: RootState) => state.canvas.zoomLevel;
export const selectScale = (state: RootState) => state.canvas.scale;
export const selectPan = (state: RootState) => ({ x: state.canvas.panX, y: state.canvas.panY });
export const selectActiveOverlays = (state: RootState) => state.canvas.activeOverlays;
export const selectIsOverlayActive = (state: RootState, layer: OverlayLayer) =>
  state.canvas.activeOverlays.includes(layer);
export const selectFocusedContextId = (state: RootState) => state.canvas.focusedContextId;
export const selectFocusedElementId = (state: RootState) => state.canvas.focusedElementId;
export const selectInteractionLog = (state: RootState) => state.canvas.interactionLog;
export const selectInteractionCount = (state: RootState) => state.canvas.interactionLog.length;
export const selectInteractionsByType = (state: RootState, type: CanvasInteractionType) =>
  state.canvas.interactionLog.filter((r) => r.type === type);

// ---------------------------------------------------------------------------
// Semantic zoom helpers
// ---------------------------------------------------------------------------

export interface ZoomLevelInfo {
  level: ZoomLevel;
  label: string;
  informationDensity: 'low' | 'medium' | 'high';
  visibleLayers: string[];
}

export const ZOOM_LEVEL_INFO: Record<ZoomLevel, ZoomLevelInfo> = {
  ecosystem: {
    level: 'ecosystem',
    label: 'Ecosystem',
    informationDensity: 'low',
    visibleLayers: ['contexts', 'connections'],
  },
  context: {
    level: 'context',
    label: 'Context',
    informationDensity: 'medium',
    visibleLayers: ['contexts', 'aggregates', 'projections', 'connections'],
  },
  element: {
    level: 'element',
    label: 'Element',
    informationDensity: 'high',
    visibleLayers: ['contexts', 'aggregates', 'projections', 'fields', 'connections', 'events'],
  },
};

export const selectZoomLevelInfo = (state: RootState): ZoomLevelInfo =>
  ZOOM_LEVEL_INFO[state.canvas.zoomLevel];
